import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  PackageOpen, Search, Plus, Trash2, Send, User, Package,
  CheckCircle, History, ChevronDown, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, printTable } from '../../lib/printUtils';

export default function DespachosPage() {
  const [mode, setMode] = useState('despacho'); // despacho | historial
  // Despacho form
  const [trabajadorSearch, setTrabajadorSearch] = useState('');
  const [trabajadorResults, setTrabajadorResults] = useState([]);
  const [selectedTrabajador, setSelectedTrabajador] = useState(null);
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialResults, setMaterialResults] = useState([]);
  const [items, setItems] = useState([]);
  const [otDestino, setOtDestino] = useState('');
  const [otSearch, setOtSearch] = useState('');
  const [otResults, setOtResults] = useState([]);
  const [showOtResults, setShowOtResults] = useState(false);
  const [tipo, setTipo] = useState('consumo');
  const [observaciones, setObservaciones] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [sending, setSending] = useState(false);
  const [showTrabResults, setShowTrabResults] = useState(false);
  const [showMatResults, setShowMatResults] = useState(false);
  // Historial
  const [despachos, setDespachos] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [busquedaGuia, setBusquedaGuia] = useState(''); // C1: buscar por N° guía

  const trabRef = useRef(null);
  const matRef = useRef(null);
  const otRef = useRef(null);

  // Search workers
  const searchTrabajador = useCallback(async (q) => {
    if (q.length < 2) { setTrabajadorResults([]); return; }
    try {
      const res = await api.get(`/bodega/buscar-trabajador?q=${encodeURIComponent(q)}`);
      setTrabajadorResults(res.data.trabajadores || []);
      setShowTrabResults(true);
    } catch { }
  }, []);

  // Search materials
  const searchMaterial = useCallback(async (q) => {
    if (q.length < 2) { setMaterialResults([]); return; }
    try {
      const res = await api.get(`/bodega/buscar-material-stock?q=${encodeURIComponent(q)}`);
      setMaterialResults(res.data.materiales || []);
      setShowMatResults(true);
    } catch { }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchTrabajador(trabajadorSearch), 300);
    return () => clearTimeout(t);
  }, [trabajadorSearch, searchTrabajador]);

  useEffect(() => {
    const t = setTimeout(() => searchMaterial(materialSearch), 300);
    return () => clearTimeout(t);
  }, [materialSearch, searchMaterial]);

  // Search OTs in execution
  const searchOt = useCallback(async (q) => {
    try {
      const res = await api.get(`/bodega/ots-en-ejecucion${q ? '?q=' + encodeURIComponent(q) : ''}`);
      setOtResults(res.data.ots || []);
      setShowOtResults(true);
    } catch { }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { if (otSearch.length >= 1) searchOt(otSearch); }, 300);
    return () => clearTimeout(t);
  }, [otSearch, searchOt]);

  // Load OTs on mount
  useEffect(() => { searchOt(''); }, [searchOt]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (trabRef.current && !trabRef.current.contains(e.target)) setShowTrabResults(false);
      if (matRef.current && !matRef.current.contains(e.target)) setShowMatResults(false);
      if (otRef.current && !otRef.current.contains(e.target)) setShowOtResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectTrabajador = (t) => {
    setSelectedTrabajador(t);
    setTrabajadorSearch(t.nombre);
    setShowTrabResults(false);
  };

  const addItem = () => {
    if (!selectedMaterial || cantidad <= 0) return;
    if (cantidad > selectedMaterial.stock_actual) {
      toast.error(`Stock insuficiente: disponible ${selectedMaterial.stock_actual}`);
      return;
    }
    setItems(prev => [...prev, {
      codigo_material: selectedMaterial.codigo,
      descripcion: selectedMaterial.descripcion2,
      cantidad: cantidad,
      stock_disponible: selectedMaterial.stock_actual,
      unidad: selectedMaterial.unidad,
      ot_numero: parseInt(otDestino) || 0,
    }]);
    setSelectedMaterial(null);
    setMaterialSearch('');
    setCantidad(1);
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const submitDespacho = async () => {
    if (items.length === 0) { toast.error('Agregue al menos un item'); return; }
    setSending(true);
    try {
      const payload = {
        trabajador_rut: selectedTrabajador?.rut || '',
        trabajador_nombre: selectedTrabajador?.nombre || '',
        trabajador_numero: selectedTrabajador?.numero || 0,
        ot_destino: parseInt(otDestino) || 0,
        tipo,
        items: items.map(i => ({ codigo_material: i.codigo_material, descripcion: i.descripcion, cantidad: i.cantidad, ot_numero: i.ot_numero })),
        observaciones,
      };
      const res = await api.post('/bodega/despacho', payload);
      toast.success(res.data.message);
      // Reset
      setItems([]);
      setSelectedTrabajador(null);
      setTrabajadorSearch('');
      setOtDestino('');
      setObservaciones('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear despacho');
    } finally {
      setSending(false);
    }
  };

  const loadHistorial = async () => {
    setHistLoading(true);
    try {
      const q = (busquedaGuia || '').trim();
      const url = q
        ? `/bodega/despachos/buscar?numero_guia=${encodeURIComponent(q)}`
        : '/bodega/despachos?limit=30';
      const res = await api.get(url);
      setDespachos(res.data.items || res.data.despachos || []);
    } catch { }
    finally { setHistLoading(false); }
  };

  useEffect(() => { if (mode === 'historial') loadHistorial(); /* eslint-disable-line */ }, [mode]);

  // Carlos (10/05/2026): imprimir y exportar despachos
  const imprimirDespachos = () => {
    if (!despachos.length) { toast.warning('No hay despachos para imprimir'); return; }
    printTable({
      title: 'Historial de Despachos de Bodega',
      subtitle: `${despachos.length} despachos · ${busquedaGuia ? `Filtro guía: "${busquedaGuia}"` : 'Últimos 30'} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total despachos', value: despachos.length },
        { label: 'Total items', value: despachos.reduce((s, d) => s + (d.total_items || d.items?.length || 0), 0) },
      ],
      columns: [
        { key: 'numero', label: 'N° Guía', mono: true, align: 'center', format: (v, r) => v || r.numero_guia || r.folio || '' },
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'ot_destino', label: 'OT', align: 'center' },
        { key: 'tipo', label: 'Tipo', align: 'center', format: (v) => (v || '').toUpperCase() },
        { key: 'total_items', label: 'Items', align: 'center', format: (v, r) => v ?? r.items?.length ?? 0 },
        { key: 'despachado_por', label: 'Despachado por' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: despachos,
    });
  };

  const exportarDespachosExcel = () => {
    if (!despachos.length) { toast.warning('No hay despachos para exportar'); return; }
    // Plana: una fila por item (más útil para análisis)
    const rows = [];
    despachos.forEach((d) => {
      const numGuia = d.numero || d.numero_guia || d.folio || '';
      if (d.items?.length) {
        d.items.forEach((it) => {
          rows.push({
            numero: numGuia,
            fecha: d.fecha,
            trabajador_nombre: d.trabajador_nombre,
            ot_destino: d.ot_destino,
            tipo: d.tipo,
            material_codigo: it.codigo,
            material_descripcion: it.descripcion,
            cantidad: it.cantidad,
            unidad: it.unidad,
            precio_unitario: it.precio_unitario,
            despachado_por: d.despachado_por,
            observaciones: d.observaciones,
          });
        });
      } else {
        rows.push({
          numero: numGuia,
          fecha: d.fecha,
          trabajador_nombre: d.trabajador_nombre,
          ot_destino: d.ot_destino,
          tipo: d.tipo,
          material_codigo: '',
          material_descripcion: `(Despacho con ${d.total_items || 0} items - sin detalle expandido)`,
          cantidad: d.total_items || 0,
          unidad: '',
          precio_unitario: 0,
          despachado_por: d.despachado_por,
          observaciones: d.observaciones,
        });
      }
    });
    exportToCSV({
      filename: 'despachos_bodega',
      title: 'CHIRINO STEEL · Historial de Despachos de Bodega',
      summary: [
        { label: 'Total despachos', value: despachos.length },
        { label: 'Total filas (items)', value: rows.length },
        { label: 'Filtro búsqueda', value: busquedaGuia || 'Últimos 30' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'numero', label: 'N° Guía' },
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'ot_destino', label: 'OT' },
        { key: 'tipo', label: 'Tipo' },
        { key: 'material_codigo', label: 'Cód. Material' },
        { key: 'material_descripcion', label: 'Descripción Material' },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'unidad', label: 'Unidad' },
        { key: 'precio_unitario', label: 'Precio Unit. (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'despachado_por', label: 'Despachado por' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows,
    });
  };

  return (
    <div className="space-y-6" data-testid="despachos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PackageOpen className="h-6 w-6" /> Despachos y Consumos
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Entrega de materiales, EPP e insumos</p>
        </div>
        <div className="flex gap-2">
          <Button variant={mode === 'despacho' ? 'default' : 'outline'} onClick={() => setMode('despacho')} data-testid="tab-despacho">
            <Send className="h-4 w-4 mr-1" /> Nuevo Despacho
          </Button>
          <Button variant={mode === 'historial' ? 'default' : 'outline'} onClick={() => setMode('historial')} data-testid="tab-historial">
            <History className="h-4 w-4 mr-1" /> Historial
          </Button>
        </div>
      </div>

      {mode === 'despacho' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Worker + OT + Type */}
            <div className="bg-card border rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Worker search */}
                <div className="relative" ref={trabRef}>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Trabajador</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Buscar nombre o RUT..."
                      value={trabajadorSearch}
                      onChange={e => { setTrabajadorSearch(e.target.value); setSelectedTrabajador(null); }}
                      data-testid="search-trabajador"
                    />
                  </div>
                  {showTrabResults && trabajadorResults.length > 0 && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {trabajadorResults.map((t, i) => (
                        <button key={i} className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                          onClick={() => selectTrabajador(t)}>
                          <span>{t.nombre}</span>
                          <span className="text-muted-foreground text-xs">{t.rut}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedTrabajador && (
                    <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> {selectedTrabajador.nombre} - {selectedTrabajador.rut}
                    </div>
                  )}
                </div>
                {/* OT */}
                <div className="relative" ref={otRef}>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">OT Destino</label>
                  <Input
                    placeholder="Buscar OT..."
                    value={otSearch}
                    onChange={e => { setOtSearch(e.target.value); setOtDestino(''); }}
                    onFocus={() => setShowOtResults(true)}
                    data-testid="ot-destino"
                  />
                  {showOtResults && otResults.length > 0 && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {otResults.filter(o => !otSearch || String(o.numero_ot).includes(otSearch) || (o.descripcion || '').toLowerCase().includes(otSearch.toLowerCase())).slice(0, 15).map((o, i) => (
                        <button key={i} className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm flex justify-between"
                          onClick={() => { setOtDestino(String(o.numero_ot)); setOtSearch(`${o.numero_ot} - ${(o.descripcion || '').slice(0, 30)}`); setShowOtResults(false); }}>
                          <span className="font-mono font-bold">{o.numero_ot}</span>
                          <span className="text-muted-foreground text-xs truncate ml-2">{(o.descripcion || '').slice(0, 35)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Type */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Tipo</label>
                  <Select value={tipo} onValueChange={setTipo}>
                    <SelectTrigger data-testid="tipo-despacho"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consumo">Consumo / Insumo</SelectItem>
                      <SelectItem value="epp">EPP</SelectItem>
                      <SelectItem value="herramienta">Herramienta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Add material */}
            <div className="bg-card border rounded-xl p-4">
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Agregar Material</label>
              <div className="flex gap-2">
                <div className="relative flex-1" ref={matRef}>
                  <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-9"
                    placeholder="Buscar material por nombre o código..."
                    value={materialSearch}
                    onChange={e => { setMaterialSearch(e.target.value); setSelectedMaterial(null); }}
                    data-testid="search-material"
                  />
                  {showMatResults && materialResults.length > 0 && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {materialResults.map((m, i) => (
                        <button key={i} className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between items-center"
                          onClick={() => { setSelectedMaterial(m); setMaterialSearch(m.descripcion2); setShowMatResults(false); }}>
                          <div>
                            <div className="font-medium">{m.descripcion2}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">{m.codigo}</div>
                          </div>
                          <span className={`text-xs font-bold ${m.stock_actual > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            Stock: {m.stock_actual}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input type="number" min="1" className="w-24" value={cantidad} onChange={e => setCantidad(Number(e.target.value))} placeholder="Cant" data-testid="cantidad" />
                <Button onClick={addItem} disabled={!selectedMaterial} data-testid="add-item-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {selectedMaterial && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Disponible: <strong>{selectedMaterial.stock_actual}</strong> {selectedMaterial.unidad} | Familia: {selectedMaterial.familia_nombre}
                </div>
              )}
            </div>

            {/* Items list */}
            {items.length > 0 && (
              <div className="bg-card border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 font-medium">Material</th>
                    <th className="text-right px-4 py-2 font-medium">Cantidad</th>
                    <th className="text-right px-4 py-2 font-medium">Stock Disp.</th>
                    <th className="px-4 py-2 w-10"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx} className="border-b" data-testid={`item-row-${idx}`}>
                        <td className="px-4 py-2">
                          <div className="font-medium text-xs">{item.descripcion}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{item.codigo_material}</div>
                        </td>
                        <td className="px-4 py-2 text-right font-bold">{item.cantidad} {item.unidad}</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">{item.stock_disponible}</td>
                        <td className="px-4 py-2">
                          <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-500">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Right: Summary + Submit */}
          <div className="space-y-4">
            <div className="bg-card border rounded-xl p-4 space-y-4 sticky top-4">
              <h3 className="font-semibold text-sm">Resumen Despacho</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Trabajador:</span><span className="font-medium">{selectedTrabajador?.nombre || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">OT Destino:</span><span className="font-medium">{otDestino || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tipo:</span><span className="font-medium capitalize">{tipo}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Items:</span><span className="font-bold text-lg">{items.length}</span></div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Observaciones</label>
                <Input placeholder="Opcional..." value={observaciones} onChange={e => setObservaciones(e.target.value)} data-testid="observaciones" />
              </div>
              <Button className="w-full" size="lg" disabled={items.length === 0 || sending} onClick={submitDespacho} data-testid="submit-despacho">
                {sending ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Confirmar Despacho
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Historial */
        <div className="space-y-3">
          {/* Buscador de guías (C1) */}
          <form
            onSubmit={(e) => { e.preventDefault(); loadHistorial(); }}
            className="flex gap-2 bg-card border rounded-xl p-3"
            data-testid="despacho-search-form"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Buscar por N° de guía de despacho… (vacío = últimos 30)"
                value={busquedaGuia}
                onChange={e => setBusquedaGuia(e.target.value)}
                data-testid="input-busqueda-guia"
              />
            </div>
            <Button type="submit" size="sm" data-testid="btn-buscar-guia">
              <Search className="h-3.5 w-3.5 mr-1" /> Buscar
            </Button>
            {busquedaGuia && (
              <Button type="button" size="sm" variant="ghost" onClick={() => { setBusquedaGuia(''); setTimeout(loadHistorial, 0); }}>
                Limpiar
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" onClick={imprimirDespachos} title="Imprimir despachos" data-testid="btn-print-despachos">
              <Printer className="h-3.5 w-3.5 mr-1" /> Imprimir
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={exportarDespachosExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-despachos">
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          </form>
        <div className="bg-card border rounded-xl overflow-hidden">
          {histLoading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : despachos.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <PackageOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay despachos registrados</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="historial-table">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium">N°</th>
                <th className="text-left px-4 py-3 font-medium">Fecha</th>
                <th className="text-left px-4 py-3 font-medium">Trabajador</th>
                <th className="text-left px-4 py-3 font-medium">Tipo</th>
                <th className="text-left px-4 py-3 font-medium">OT</th>
                <th className="text-right px-4 py-3 font-medium">Items</th>
                <th className="text-left px-4 py-3 font-medium">Despachado por</th>
              </tr></thead>
              <tbody>
                {despachos.map((d, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/30" data-testid={`despacho-row-${idx}`}>
                    <td className="px-4 py-2.5 font-mono font-bold">{d.numero}</td>
                    <td className="px-4 py-2.5 text-xs">{new Date(d.fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td className="px-4 py-2.5">{d.trabajador_nombre || '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        d.tipo === 'epp' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                        d.tipo === 'herramienta' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                      }`}>{d.tipo}</span>
                    </td>
                    <td className="px-4 py-2.5">{d.ot_destino || '-'}</td>
                    <td className="px-4 py-2.5 text-right font-bold">{d.total_items}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{d.despachado_por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>
      )}
    </div>
  );
}
